import { motion } from "framer-motion";
import { Scan, LockOpen, VolumeX } from "lucide-react";
import { useRef, useEffect } from "react";
import { useInView } from "react-intersection-observer";

const steps = [
  {
    icon: Scan,
    title: "Scan",
    description: "Follow the instructions on the pod to book"
  },
  {
    icon: LockOpen,
    title: "Unlock",
    description: "Get your access code to enter the pod"
  },
  {
    icon: VolumeX,
    title: "Focus",
    description: "Enjoy your private workspace"
  }
];

const HowItWorks = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.2,
  });

  // Play/pause video based on visibility
  useEffect(() => {
    if (videoRef.current) {
      if (inView) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [inView]);

  return (
    <section id="how-it-works" className="w-full bg-background py-24 overflow-hidden">
      <div className="container px-4 mx-auto">
        <div className="grid grid-cols-1 laptop:grid-cols-2 desktop:grid-cols-2 gap-12 laptop:gap-16 desktop:gap-20 items-center">
          
          {/* Left Column - Video */}
          <motion.div
            ref={inViewRef}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="order-1 laptop:order-1 desktop:order-1"
          >
            <video 
              ref={videoRef}
              muted 
              loop 
              playsInline
              preload="auto"
              className="w-4/5 mx-auto"
            >
              <source src="/videos/how-it-works.mov" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </motion.div>

          {/* Right Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="order-2 laptop:order-2 desktop:order-2"
          >
            <div className="space-y-8">
              {steps.map((step, index) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-left md:justify-start"
                >
                  <div className="flex-shrink-0 w-14 h-14 bg-card rounded-2xl flex items-center justify-center shadow-lg border border-border/50">
                    <step.icon className="w-7 h-7 text-primary" />
                  </div>
                  <div className="flex flex-col justify-center items-center md:items-start">
                    <h3 className="font-funnel text-2xl font-bold text-foreground mb-1">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground text-base">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default HowItWorks;